import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useMemo,
} from 'react'
import { useSaleManager } from '../../../ng-compat/hooks'
import { getFeatureFromCache } from '../../../util/feature-cache'
import { FEATURE_DISABLE_PAYMENT_BUTTONS_DURING_SALE_RECALCULATION } from '../../../constants'

type PaymentRecalculationLockContextValue = {
  arePaymentActionsLocked: boolean
  lockPaymentActionsUntilRecalculationCompletes: () => void
}

const defaultContextValue: PaymentRecalculationLockContextValue = {
  arePaymentActionsLocked: false,
  lockPaymentActionsUntilRecalculationCompletes: () => {},
}

const PaymentRecalculationLockContext = createContext<PaymentRecalculationLockContextValue>(
  defaultContextValue
)

export const PaymentRecalculationLockProvider = ({
  children,
}: {
  children: ReactNode
}) => {
  const saleManager = useSaleManager()
  const shouldDisablePaymentButtonsDuringStateRecalculation = getFeatureFromCache(
    FEATURE_DISABLE_PAYMENT_BUTTONS_DURING_SALE_RECALCULATION
  )

  const [arePaymentActionsLocked, setArePaymentButtonsDisabled] = useState(
    false
  )

  const lockPaymentActionsUntilRecalculationCompletes = useCallback(() => {
    if (shouldDisablePaymentButtonsDuringStateRecalculation) {
      setArePaymentButtonsDisabled(true)
    }
  }, [shouldDisablePaymentButtonsDuringStateRecalculation])

  useEffect(() => {
    if (!shouldDisablePaymentButtonsDuringStateRecalculation) {
      return
    }
    const onCalculatingEnd = () => setArePaymentButtonsDisabled(false)
    saleManager.on('saleManager:saleCalculating:end', onCalculatingEnd)
    return () => {
      saleManager.off('saleManager:saleCalculating:end', onCalculatingEnd)
    }
  }, [saleManager, shouldDisablePaymentButtonsDuringStateRecalculation])

  const value = useMemo(
    () => ({
      arePaymentActionsLocked,
      lockPaymentActionsUntilRecalculationCompletes,
    }),
    [arePaymentActionsLocked, lockPaymentActionsUntilRecalculationCompletes]
  )

  return (
    <PaymentRecalculationLockContext.Provider value={value}>
      {children}
    </PaymentRecalculationLockContext.Provider>
  )
}

export const usePaymentRecalculationLock = () => {
  return useContext(PaymentRecalculationLockContext)
}
